import { Box, Checkbox, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import { GoPlus } from 'react-icons/go';
import { Link, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import {
  useCloneKnowledgeBase,
  useDeleteKnowledgeBase,
  useGetKnowledgeBases
} from 'apis/business/setting/knowledgeBase/hook';
import { useEffect, useState } from 'react';
import { useGetAgent, useUpdateAgentConfig } from 'apis/business/agent/hook';
import { MdOutlineCalendarToday, MdOutlineMoreVert, MdOutlineTextSnippet } from 'react-icons/md';
import { formatDate } from 'utils/helperFunctions';
import { EmptyStateView } from 'components/Common/EmptyStateView';
import BusinessKnowledgeSkeleton from './BusinessKnowledgeSkeleton';

export default function BusinessKnowledge() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    id: number | null;
  }>({
    anchorEl: null,
    id: null
  });
  const { data: knowledgeBases, isLoading } = useGetKnowledgeBases();
  const { data: agent } = useGetAgent();
  const { mutateAsync: updateAgent } = useUpdateAgentConfig();
  const { mutate: cloneKnowledgeBase } = useCloneKnowledgeBase();
  const { mutateAsync: deleteKnowledgeBase } = useDeleteKnowledgeBase();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setMenuState({ anchorEl: event.currentTarget, id });
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, id: null });
  };

  const handleDelete = (id: number) => {
    deleteKnowledgeBase(String(id));
    handleClose();
  };

  const handleCheckboxChange = async (knowledgeBaseId: number) => {
    const updatedItems = checkedItems.includes(knowledgeBaseId)
      ? checkedItems.filter((itemId) => itemId !== knowledgeBaseId)
      : [...checkedItems, knowledgeBaseId];

    setCheckedItems(updatedItems);

    const payload = {
      agent: {
        knowledgebasis_ids: updatedItems
      }
    };
    await updateAgent(payload);
  };

  useEffect(() => {
    if (agent?.knowledgebasis_ids) {
      setCheckedItems(agent.knowledgebasis_ids);
    }
  }, [agent]);

  if (isLoading) {
    return <BusinessKnowledgeSkeleton />;
  }

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 1.5, lg: 0 }}
        mt={4}
      >
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Connect Knowledge Base
        </Typography>

        <Button
          color='primary'
          startIcon={<GoPlus />}
          sx={{
            whiteSpace: 'nowrap',
            px: 2,
            mt: { xs: 1, lg: 0 },
            width: { xs: '100%', sm: 'auto' }
          }}
          onClick={() => navigate('add')}
        >
          Add
        </Button>
      </Box>
      {knowledgeBases?.data?.length === 0 && <EmptyStateView />}
      <Grid container spacing={2} mt={3}>
        {knowledgeBases?.data.map((item, index) => (
          <Grid
            key={index}
            size={{ xs: 12, sm: 6, lg: 4 }}
            bgcolor={theme.palette.background.paper}
            border='1px solid #F3F3F3'
            borderRadius={0.5}
            padding={{ xs: 2, lg: 2 }}
          >
            <Box display='flex' justifyContent='space-between' alignItems='center' gap={{ xs: 1, lg: 0 }}>
              <Box display='flex' alignItems='center' gap={1}>
                <Checkbox
                  value={item.id}
                  checked={checkedItems.includes(item.id)}
                  onChange={() => handleCheckboxChange(item.id)}
                  sx={{ padding: 0 }}
                />
                <Link
                  to={`/setting/knowledge-bases/${item.id}/dataset`}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Typography
                    variant='h3'
                    fontSize={18}
                    fontWeight={500}
                    color={theme.palette.primary.main}
                    sx={{ fontSize: { xs: 16, lg: 18 } }}
                  >
                    {item.name}
                  </Typography>
                </Link>
              </Box>
              <IconButton
                id='basic-button'
                aria-controls={menuState.id === item.id ? 'basic-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={menuState.id === item.id ? 'true' : undefined}
                onClick={(event) => handleClick(event, item.id)}
                sx={{
                  p: { xs: 0.75, lg: 1 }
                }}
              >
                <MdOutlineMoreVert />
              </IconButton>
              <Menu
                id={`basic-menu-${item.id}`}
                anchorEl={menuState.anchorEl}
                open={menuState.id === item.id}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button'
                }}
                slotProps={{ paper: { sx: { width: 120 } } }}
              >
                <Link to={`/setting/knowledge-bases/${item.id}/dataset`}>
                  <MenuItem onClick={handleClose}>Edit</MenuItem>
                </Link>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    cloneKnowledgeBase(item.id);
                  }}
                >
                  Dulplicate
                </MenuItem>
                <Link to={`${item.id}/edit`}>
                  <MenuItem onClick={handleClose}>Rename</MenuItem>
                </Link>
                <MenuItem onClick={() => handleDelete(item.id)}>Delete</MenuItem>
              </Menu>
            </Box>
            <Typography color='secondary' variant='body2' marginBottom={3} marginTop={1}>
              {item.description ?? 'For it we need to study and validate test cases'}
            </Typography>
            <Box color={theme.palette.secondary.main} display='flex' gap={1} alignItems='center'>
              <MdOutlineTextSnippet />
              <Typography variant='body2' color='#000'>
                {item.datasets?.length} Docs
              </Typography>
            </Box>
            <Box display='flex' gap={1} alignItems='center' marginTop={1}>
              <MdOutlineCalendarToday />
              <Typography variant='body2'>{formatDate(item.created_at)}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
